// PH-49 — which shop a device/tab belongs to.
//
// Pattern C (see PLAN.md "Multi-Tenant Routing Decision") deliberately does NOT wrap every
// route under a `/:tenant/` prefix — that's "Pattern A", evaluated and rejected as more
// refactor than it's worth. This module is narrower: `/s/<slug>/` is a one-time INSTALL
// ANCHOR, not a routing scheme. It exists only so a per-tenant manifest.json can give an
// installed home-screen icon a `start_url` that survives `localStorage`/site-data being
// cleared (a user action, or iOS evicting idle PWA storage) — something a query string alone
// cannot do, because nothing is left to read it from once storage is gone. The moment the
// slug is captured it's normalized away, same as `?t=` already was, and the app goes right
// back to Pattern C's flat routes.
//
// Resolution order, most-trustworthy first: URL path segment (baked into the installed
// icon, survives storage clears) → `?t=` query param (existing invite-link mechanism,
// requires the link) → previously-stored slug (last resort — nothing in this URL says
// anything).
//
// Pure — no DOM/localStorage access — so the tenant-switch logic (multi-tenant, so a wrong
// answer here means one shop's data rendering under another shop's identity) is unit
// -testable without a browser environment.

import { rememberShop } from './knownShops';

const PATH_TENANT_RE = /^\/s\/([a-z0-9-]{1,64})(\/.*)?$/i;

// Mirrors the backend's _SLUG_RE (app/routers/tenant.py) — alnum start/end, hyphens only in
// the middle, capped at 64 (the tenant_id DB column width). Applied to BOTH capture sources
// (path and ?t=) so a malformed slug is never silently written to localStorage and never
// silently produces a manifest link nobody can trace back to a cause — a PR-review finding
// (confirmed real): the two sources had different, looser rules, and a slug that failed only
// the backend's check still landed in localStorage from the frontend, with nothing to say why
// the resulting manifest fell back to the generic shop.
const SLUG_SHAPE_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

function normalizeSlug(raw) {
  const s = (raw || '').trim().toLowerCase();
  return SLUG_SHAPE_RE.test(s) ? s : '';
}

/**
 * @param {{ pathname: string, search: string, storedSlug: string }} loc
 * @returns {{
 *   slug: string,            // the effective tenant slug for this load ('' if none known at all)
 *   incomingSlug: string,    // slug carried by THIS url (path or query), '' if neither present
 *                            // or present-but-malformed (treated as absent, not stripped —
 *                            // leaving a bad link visibly wrong beats silently swallowing it)
 *   switched: boolean,       // incomingSlug present and differs from storedSlug — caller should drop cached tenantConfig/session
 *   notifySwitch: boolean,   // like `switched`, but ALSO requires a previously-known slug — see below
 *   normalizedPath: string,  // pathname with any /s/<slug> prefix stripped
 *   normalizedSearch: string,// search with ?t= stripped
 *   urlChanged: boolean,     // whether normalizedPath/normalizedSearch differ from the input and the address bar should be rewritten
 * }}
 */
export function resolveTenantSlug({ pathname, search, storedSlug }) {
  const pathMatch = pathname.match(PATH_TENANT_RE);
  const pathSlug = pathMatch ? normalizeSlug(pathMatch[1]) : '';

  const params = new URLSearchParams(search);
  const queryRaw = params.get('t');
  const querySlug = normalizeSlug(queryRaw);

  // Path wins over query when a link somehow carries both.
  const incomingSlug = pathSlug || querySlug;
  const prevSlug = (storedSlug || '').trim().toLowerCase();
  const slug = incomingSlug || prevSlug;
  // Deliberately does NOT require prevSlug to be non-empty: on a brand-new device, or the exact
  // clear-site-data-then-relaunch this ticket exists to fix, prevSlug IS empty — but any cached
  // tenantConfig/session left over from some earlier, inconsistent state must still be dropped,
  // and it's cheap/safe to always do so whenever a slug arrives from the URL.
  const switched = Boolean(incomingSlug) && incomingSlug !== prevSlug;
  // A narrower question than `switched`, for the UI notice only (a Codex PR-review finding,
  // confirmed real): on that exact same brand-new-device / just-cleared-storage load, there was
  // no PREVIOUS shop to switch away from, so telling the user "this device now points to a
  // different shop" is actively wrong on the very success path PH-49 exists to fix — the icon
  // survived the clear and correctly reopened to the same shop; nothing was switched from the
  // user's perspective. Only announce it when a genuinely different, previously-known tenant is
  // being replaced.
  const notifySwitch = switched && Boolean(prevSlug);

  let normalizedPath = pathname;
  let normalizedSearch = search;
  const urlChanged = Boolean(incomingSlug);
  if (urlChanged) {
    normalizedPath = pathMatch ? (pathMatch[2] || '/') : pathname;
    params.delete('t');
    const qs = params.toString();
    normalizedSearch = qs ? `?${qs}` : '';
  }

  return { slug, incomingSlug, switched, notifySwitch, normalizedPath, normalizedSearch, urlChanged };
}

/**
 * Applies resolveTenantSlug()'s decision to the real browser: persists/clears localStorage,
 * normalizes the address bar, and points the installed-icon manifest link at the resolved
 * tenant. Dependencies are injectable so this can run against a fake DOM in tests without
 * touching `window`/`document`/`localStorage` for real — App.jsx's module-level IIFE just
 * calls this with no arguments.
 */
export function applyTenantResolution({
  location = window.location,
  storage = window.localStorage,
  history = window.history,
  doc = document,
} = {}) {
  const storedSlug = storage.getItem('pos_tenant_id') || '';
  const { slug, incomingSlug, switched, notifySwitch, normalizedPath, normalizedSearch, urlChanged } =
    resolveTenantSlug({ pathname: location.pathname, search: location.search, storedSlug });

  if (incomingSlug) {
    // Write the slug FIRST, before clearing anything (a PR-review finding, confirmed real —
    // and worth taking seriously given this whole ticket is about storage reliability):
    // storage.setItem can throw (Safari private browsing, a full quota) even when getItem/
    // removeItem would have succeeded, since only a WRITE needs headroom. Old order cleared
    // tenantConfig/session, then wrote the slug — if THAT write then threw, the device was left
    // with the OLD pos_tenant_id (the failed write never landed) but NO cached config, so the
    // next render would silently re-fetch and re-render the OLD tenant under a half-cleared
    // state instead of either genuinely switching or cleanly staying put. Writing first means a
    // throw here leaves storage completely untouched — the device stays cleanly on whatever
    // tenant it already had, which is what "best-effort" should actually mean on a multi-tenant
    // identity path.
    storage.setItem('pos_tenant_id', incomingSlug);
    if (switched) {
      // Only reachable once the write above has already succeeded, so a throw from removeItem
      // (much less likely — freeing space, not requesting it) is the only remaining risk, and
      // it fails no worse than before this reordering.
      storage.removeItem('tenantConfig');
      storage.removeItem('session');
    }
    // PH-50 — this is the one place a shop's link is actually opened, so it is the one place a
    // shop joins the device's switcher list (utils/knownShops.js says why nowhere else may). The
    // shop being switched AWAY from goes on first, so the one just opened ends up at the front.
    // Best-effort and last: the list is a convenience, so a failure here must never undo or
    // block the tenant switch that has already landed above.
    try {
      if (switched && storedSlug) rememberShop(storage, storedSlug);
      rememberShop(storage, incomingSlug);
    } catch (_) { /* full storage / private browsing — the switcher just won't list it */ }
  }

  // Update the manifest link BEFORE rewriting the address bar (also reordered — a PR-review
  // finding): some browsers may re-evaluate PWA installability when the URL changes via
  // history.replaceState, and doing this the other way around left a narrow window where the
  // address bar already read "/" but the manifest link still pointed at the generic one.
  // Reversing costs nothing — the link's href doesn't depend on the address bar.
  //
  // Known separate timing gap (also a PR-review finding): this still runs when the JS bundle
  // executes, which is AFTER index.html's <head> is parsed — a browser that eagerly fetches the
  // manifest at parse time, before this line ever runs, would fetch the generic static one. In
  // practice this is narrow: an actual "Add to Home Screen" is a deliberate, multi-second user
  // action, by which point this has long since run (synchronous, at module load, before React
  // even mounts), and browsers re-read the CURRENT <link> href when that action fires rather
  // than caching from initial parse. Not eliminated, though — only closable by moving this
  // logic into an inline, blocking <script> in index.html's own <head>, ahead of the bundle
  // load, which is a bigger change than this ticket's scope; ADR-008 already tracks browser/OS
  // manifest-support variance as outside this repository's control for the same reason.
  if (slug) {
    const link = doc.querySelector('link[rel="manifest"]');
    if (link) link.href = `/api/tenant/manifest?tenant_id=${encodeURIComponent(slug)}`;
  }

  if (urlChanged) {
    // Preserve the fragment — replaceState with a URL that omits it clears whatever #hash was
    // already in the address bar (SetupWizard's own #token= invite links, the hidden #/owner
    // panel route). No link combines a tenant slug with a hash today, but this normalization
    // now fires on two triggers (?t= and /s/<slug>) instead of one, so keep it safe either way.
    const hash = location.hash || '';
    history.replaceState(null, '', normalizedPath + normalizedSearch + hash);
  }

  return { slug, incomingSlug, switched, notifySwitch };
}
